import { Task } from '@models/task.model';
import { parseDateOnly } from '@utils/formatDate';

export type ClientTaskGroupId =
  | 'awaiting'
  | 'inProgress'
  | 'upcoming'
  | 'complete';

export const CLIENT_TASK_GROUP_ORDER: ClientTaskGroupId[] = [
  'awaiting',
  'inProgress',
  'upcoming',
  'complete',
];

/**
 * Sprint 44, story-226. Display classification of one task row by its own status. CANCELED,
 * REJECTED and DELEGATION_CANCELED are not shown to a client (`null`); INTERNAL_APPROVAL is
 * internal workflow and shows as plain "in progress". Aggregate numbers (header, chips) are
 * never derived from this: they are the server's.
 */
export const classifyClientTask = (
  status: string | undefined,
): ClientTaskGroupId | null => {
  switch (status) {
    case 'CLIENT_APPROVAL':
      return 'awaiting';
    case 'ACTIVE':
    case 'INTERNAL_APPROVAL':
      return 'inProgress';
    case 'INACTIVE':
      return 'upcoming';
    case 'COMPLETE':
      return 'complete';
    default:
      return null;
  }
};

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const time = (value: Task['endTime'] | Task['startTime']): number | null => {
  if (!value) return null;
  const date = parseDateOnly(value as string | Date);
  return Number.isNaN(date.getTime()) ? null : startOfDay(date);
};

/** Whole days past this row's own end date; `null` unless not complete and past due. */
export const daysLateFor = (
  task: Pick<Task, 'status' | 'endTime'>,
  today: Date = new Date(),
): number | null => {
  if (task.status === 'COMPLETE') return null;
  const end = time(task.endTime);
  if (end === null) return null;
  const days = Math.round((startOfDay(today) - end) / 86_400_000);
  return days > 0 ? days : null;
};

const compareNullable = (a: number | null, b: number | null): number =>
  a === b ? 0 : a === null ? 1 : b === null ? -1 : a - b;

/**
 * Ordering inside a group (only when schedule dates are shown; otherwise the server's order):
 * awaiting by end asc; in progress overdue first (most late first) then end asc; upcoming by
 * start asc; complete by end desc.
 */
export const sortGroupTasks = (
  group: ClientTaskGroupId,
  tasks: Task[],
  showDates: boolean,
  today: Date = new Date(),
): Task[] => {
  if (!showDates) return tasks;
  const copy = [...tasks];
  switch (group) {
    case 'awaiting':
      return copy.sort((a, b) =>
        compareNullable(time(a.endTime), time(b.endTime)),
      );
    case 'inProgress':
      return copy.sort(
        (a, b) =>
          (daysLateFor(b, today) ?? 0) - (daysLateFor(a, today) ?? 0) ||
          compareNullable(time(a.endTime), time(b.endTime)),
      );
    case 'upcoming':
      return copy.sort((a, b) =>
        compareNullable(time(a.startTime), time(b.startTime)),
      );
    default:
      return copy.sort((a, b) =>
        compareNullable(time(b.endTime), time(a.endTime)),
      );
  }
};
